/**
 * PkRueckmeldungDialog - reply to the insurer that the police deviates from the Antrag (P2 hardening 05).
 * The `to` is prefilled from the server's recipient ladder but stays EDITABLE and required (the ladder
 * can pick a wrong address); the body is prefilled with buildPkRueckmeldungText and is fully editable.
 * The send bypasses the staged Freigabe - it goes immediately from the caller's mailbox.
 */
import { useEffect, useRef, useState } from 'react';
import { PK_RUECKMELDUNG_BESTAETIGUNG, PK_RUECKMELDUNG_ERNEUT_HINWEIS, PK_RUECKMELDUNG_TEXT_MIN, PK_RUECKMELDUNG_TEXT_MAX } from '@brokly/shared';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';

const CODE_TEXT: Record<string, string> = {
  communication_send_required: 'Fuer den E-Mail-Versand fehlt die Berechtigung (communication.send).',
  keine_abweichung: 'Die aktuelle Kontrolle weist keine Abweichung aus.',
  bereits_abgeschlossen: 'Die Ausschreibung ist bereits abgeschlossen.',
  empfaenger_fehlt: 'Fuer den Versicherer ist keine E-Mail-Adresse hinterlegt - bitte eintragen.',
  in_reply_to_invalid: 'Die Bezugsnachricht passt nicht zu dieser Police.',
  text_required: 'Bitte einen Text mit mindestens 10 Zeichen erfassen.',
  mailbox_not_connected: 'Ihr Postfach ist nicht verbunden.',
  mailbox_reauth_required: 'Ihr Postfach muss neu verbunden werden.',
  mailbox_send_failed: 'Der Versand ist fehlgeschlagen.',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PkRueckmeldungDialog({
  open,
  onClose,
  onSend,
  saving,
  error,
  defaultTo,
  defaultText,
  bereitsGesendet,
}: {
  open: boolean;
  onClose: () => void;
  onSend: (payload: { to: string; text: string }) => void;
  saving: boolean;
  error: { code?: string } | null;
  defaultTo: string | null;
  defaultText: string;
  bereitsGesendet: boolean;
}) {
  const [to, setTo] = useState(defaultTo ?? '');
  const [text, setText] = useState(defaultText);

  // m6: re-seed ONLY on the closed->open transition (a fresh Eingang/Kontrolle). A background
  // refetch that changes defaultTo/defaultText while the dialog is open must NOT overwrite what the
  // broker is typing.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setTo(defaultTo ?? '');
      setText(defaultText);
    }
    wasOpen.current = open;
  }, [open, defaultTo, defaultText]);

  const toOk = EMAIL_RE.test(to.trim());
  const textOk = text.trim().length >= PK_RUECKMELDUNG_TEXT_MIN && text.trim().length <= PK_RUECKMELDUNG_TEXT_MAX;
  const errorText = error?.code ? CODE_TEXT[error.code] ?? 'Rueckmeldung fehlgeschlagen' : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Rueckmeldung an Versicherer"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button disabled={saving || !toOk || !textOk} onClick={() => onSend({ to: to.trim(), text: text.trim() })} data-testid="pk-rueckmeldung-send">
            An Versicherer senden
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm" data-testid="pk-rueckmeldung-dialog">
        <p className="text-xs text-muted-foreground">{PK_RUECKMELDUNG_BESTAETIGUNG}</p>
        {bereitsGesendet && <p className="text-xs text-status-warning" data-testid="pk-rueckmeldung-erneut">{PK_RUECKMELDUNG_ERNEUT_HINWEIS}</p>}
        <label className="block">
          <span className="text-xs text-muted-foreground">Empfaenger (Versicherer)</span>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 w-full rounded-md border border-border p-2"
            data-testid="pk-rueckmeldung-to"
          />
          {!toOk && to.length > 0 && <span className="text-xs text-status-danger">Bitte eine gueltige E-Mail-Adresse angeben.</span>}
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Nachricht</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            maxLength={PK_RUECKMELDUNG_TEXT_MAX}
            className="mt-1 w-full rounded-md border border-border p-2 font-mono text-xs"
            data-testid="pk-rueckmeldung-text"
          />
          <span className="text-xs text-muted-foreground">{text.trim().length} / {PK_RUECKMELDUNG_TEXT_MAX}</span>
        </label>
        {errorText && <p className="text-sm text-status-danger" data-testid="pk-rueckmeldung-error">{errorText}</p>}
      </div>
    </Dialog>
  );
}
